import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Car, 
  Gamepad2, 
  Smartphone, 
  Settings,
  Zap,
  RotateCcw,
  Keyboard
} from 'lucide-react'
import JoystickControl from './JoystickControl'
import ButtonControl from './ButtonControl'
import KeyboardControl from './KeyboardControl'
import SpeedControl from './SpeedControl'

const CarControlPanel = ({
  isConnected,
  controlMode,
  setControlMode,
  sendCarCommand,
  currentDirection,
  currentSpeed,
  isMoving
}) => {
  const [showHelp, setShowHelp] = useState(false)

  const handleControlModeChange = (mode) => {
    setControlMode(mode)
  }

  const getCarRotation = () => {
    switch (currentDirection) {
      case 'LEFT':
        return -12
      case 'RIGHT':
        return 12
      case 'LEFT-FORWARD':
        return -6
      case 'RIGHT-FORWARD':
        return 6
      case 'LEFT-BACKWARD':
        return -6
      case 'RIGHT-BACKWARD':
        return 6
      default:
        return 0
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              animate={{ 
                rotate: getCarRotation(),
                scale: isMoving ? 1.1 : 1
              }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <Car className="w-12 h-12 text-primary-400" />
              {isMoving && (
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="absolute inset-0 bg-primary-400/20 rounded-full blur-sm"
                />
              )}
            </motion.div>
            <div>
              <h2 className="text-2xl font-bold text-glow">Car Control</h2>
              <p className="text-gray-400">
                {isConnected ? 'Connected and ready' : 'Not connected'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="btn-secondary flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Help</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Control Mode Selector */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-4"
      >
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => handleControlModeChange('joystick')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              controlMode === 'joystick'
                ? 'bg-primary-600 text-white shadow-glow'
                : 'bg-secondary-600 text-gray-300 hover:bg-secondary-700'
            }`}
          >
            <Gamepad2 className="w-4 h-4" />
            <span>Joystick</span>
          </button>
          
          <button
            onClick={() => handleControlModeChange('buttons')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              controlMode === 'buttons'
                ? 'bg-primary-600 text-white shadow-glow'
                : 'bg-secondary-600 text-gray-300 hover:bg-secondary-700'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            <span>Buttons</span>
          </button>
          
          <button
            onClick={() => handleControlModeChange('keyboard')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              controlMode === 'keyboard'
                ? 'bg-primary-600 text-white shadow-glow'
                : 'bg-secondary-600 text-gray-300 hover:bg-secondary-700'
            }`}
          >
            <Keyboard className="w-4 h-4" />
            <span>Keyboard</span>
          </button>
        </div>
      </motion.div>

      {/* Control Area */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        {controlMode === 'joystick' ? (
          <JoystickControl
            isConnected={isConnected}
            sendCarCommand={sendCarCommand}
            currentDirection={currentDirection}
            isMoving={isMoving}
          />
        ) : controlMode === 'buttons' ? (
          <ButtonControl
            isConnected={isConnected}
            sendCarCommand={sendCarCommand}
            currentDirection={currentDirection}
            isMoving={isMoving}
          />
        ) : (
          <KeyboardControl
            isConnected={isConnected}
            sendCarCommand={sendCarCommand}
            currentDirection={currentDirection}
            isMoving={isMoving}
          />
        )}
      </motion.div>

      {/* Speed Control */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <SpeedControl
          isConnected={isConnected}
          currentSpeed={currentSpeed}
          sendCarCommand={sendCarCommand}
        />
      </motion.div>

      {/* Help Modal */}
      {showHelp && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowHelp(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="card p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4 text-glow">Pin Configuration Help</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span>D1 (GPIO5):</span>
                <span className="text-primary-400">Left Motor Forward (IN1)</span>
              </div>
              <div className="flex justify-between">
                <span>D2 (GPIO4):</span>
                <span className="text-primary-400">Left Motor Backward (IN2)</span>
              </div>
              <div className="flex justify-between">
                <span>D3 (GPIO0):</span>
                <span className="text-primary-400">Right Motor Forward (IN3)</span>
              </div>
              <div className="flex justify-between">
                <span>D4 (GPIO2):</span>
                <span className="text-primary-400">Right Motor Backward (IN4)</span>
              </div>
              <div className="flex justify-between">
                <span>D5 (GPIO14):</span>
                <span className="text-primary-400">Motor Speed Control (ENA/ENB)</span>
              </div>
              <div className="flex justify-between">
                <span>D6 (GPIO12):</span>
                <span className="text-primary-400">Status LED (Optional)</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-primary-500/20 rounded-lg">
              <p className="text-xs text-primary-300">
                <strong>Note:</strong> Make sure your NodeMCU is powered with 12V and 
                the L298N motor driver is properly connected to both motors.
              </p>
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="btn-primary w-full mt-4"
            >
              Got it!
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default CarControlPanel 